import CONSTANTS from "@/lib/CONSTANTS";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { redirectToLogin, isLocalhost, isCustomerLoggedIn } from "@maersk-global/shared-js";
import { firstLetterUpperCase } from "@/lib/filters";

dayjs.extend(utc);

/**
 * @name getPartyRoles
 * @description return the subset of USER_ROLES for the given party codes(import, export, so on).
 * Usage examples:
 * getPartyRoles([44, 4, 32, 5, 6, 16, 12, 31, 39, 41])
 * @param {Number[]} partiesCodes A set of unique party codes
 * @return {Map}
 */
export function getPartyRoles(partiesCodes) {
  const rolesSubset = new Map();
  CONSTANTS.USER_ROLES.forEach((label, code) => {
    if (partiesCodes.includes(code)) {
      rolesSubset.set(code, label);
    }
  });
  return rolesSubset;
}

export function multipleContainerTypesSizes(containerTypes, containerSizes) {
  return containerTypes?.size > 0 && containerSizes?.size > 0;
}

export function oneContainerType(containerTypes) {
  return containerTypes.size === 1 && !containerTypes.has(undefined);
}

export function oneContainerSize(containerSizes) {
  return containerSizes.size === 1 && !containerSizes.has(undefined);
}

export function containerDetails(containerTypes, containerSizes, containersNames) {
  if (containerTypes.size === 1 && [...containerTypes][0] && containerSizes.size === 1 && [...containerSizes][0]) {
    if (containersNames.size === 1 && [...containersNames][0]) {
      return [...containersNames][0];
    } else {
      return `${[...containerSizes][0]}'` + " " + firstLetterUpperCase([...containerTypes][0]);
    }
  } else if (containerTypes.size > 1 || containerSizes.size > 1) {
    return CONSTANTS.CARGO_VARIOUS_CONTAINERS;
  } else {
    return "-";
  }
}

/**
 * @name getContainerNameForShipment
 * @description Gets the container's display name from the given array of all container types, all container sizes and all container display names of a shipment.
 * @param containerTypes
 * @param containerSizes
 * @param containersNames
 */
export function getContainerNameForShipment(containerTypes, containerSizes, containersNames) {
  if (multipleContainerTypesSizes(containerTypes, containerSizes)) {
    return containerDetails(containerTypes, containerSizes, containersNames);
  } else if (oneContainerType(containerTypes)) {
    return "-" + " " + firstLetterUpperCase([...containerTypes][0]);
  } else if (oneContainerSize(containerSizes)) {
    return `${[...containerSizes][0]}'` + " " + "-";
  } else {
    return "-";
  }
}

/**
 * @name isUserLogged
 * @description Checks several conditions to establish if there is a logged user: 1) is the environment localhost (for local development); 2) is there a JWT that has no error, has an expiration date and whose expiration is after the present moment.
 * @return {boolean}
 */
export function isUserLogged() {
  return isLocalhost() || isCustomerLoggedIn();
}

/**
 * @name checkVessel
 * @description checks if the arriving and departing vessel are the same and only displays the departing vessel if true, otherwise it displays both
 * @param departingVessel: name of the departing vessel
 * @param arrivingVessel: name of the arriving vessel
 * @return String
 */

export function checkVessel(departingVessel, arrivingVessel) {
  if (!departingVessel && !arrivingVessel) return "-";
  if (!arrivingVessel) return departingVessel;
  if (!departingVessel) return arrivingVessel;
  return departingVessel === arrivingVessel ? departingVessel : `${departingVessel} | ${arrivingVessel}`;
}

/**
 * @name dateComparator
 * @description Compares two date-like strings, can be used in an array sort as it returns 1/-1/0
 * @param {String} first The first date
 * @param {String} second The second date
 * @param {String} ordering a string, "DESCENDING" or "ASCENDING" - default is ASCENDING
 */
export function dateComparator(first, second, ordering) {
  if (first === second) return 0;
  if (first === undefined || first === null) return 1;
  if (second === undefined || second === null) return -1;
  return ordering === "DESCENDING" ? dayjs(second) - dayjs(first) : dayjs(first) - dayjs(second);
}

/**
 * @name getExportImportPage
 * @description checks if the customer is on Import or Export
 * @return String.
 */
export function getExportImportPage() {
  const pathname = globalThis.location?.pathname;

  if (pathname?.includes("export")) {
    return "EXP";
  }

  if (pathname?.includes("import")) {
    return "IMP";
  }

  return "ETA changes";
}

/**
 * This function returns a company name from [iam]userSessionInfo
 * it is located in local storage
 * @returns {string}
 */

export const getCompany = () => {
  if (isLocalhost()) return "Boringly Named Shipment Company";
  const storageValue = localStorage.getItem("[iam]userSessionInfo");
  if (!storageValue) return "";
  const data = storageValue.slice(1, storageValue.length);
  return JSON.parse(window.atob(data))["customerName"];
};

/**
 * handleErrors check if the response ok is true or false
 * if it's true it returns the response
 * if it's false it throws an exception
 * @returns response or throws an exception
 */
export const handleErrors = (response) => {
  if (response?.status === 401 || response?.type === "opaqueredirect" || response?.status === 403) {
    redirectToLogin(`${CONSTANTS.APP_BASE}/`);
    return;
  } else if (!response.ok) {
    throw response;
  } else {
    return response;
  }
};

/**
 * Calls data tracking function
 * @param {*} action name of tracking action
 * @param {*} data data to be logged
 */
export function trackData(action, pensieve) {
  window.faro?.api?.pushEvent?.(action, pensieve);
}

/**
 * Checks local storage for the given properties and local storage key
 * @param {Array} array of options
 * @param {String} the locals storage key
 * @returns set option or null
 */
export function getOptionsFromLocalStorage(options, localStorageKey) {
  if (localStorage.getItem(localStorageKey) && options.includes(localStorage.getItem(localStorageKey))) {
    return localStorage.getItem(localStorageKey);
  }
  return false;
}

export function trackShipmentClick(tab, isExport) {
  globalThis.dataLayer.push({
    event: "DQCS",
    category: "SO_ShipmentRow",
    action: "Click",
    label: {
      data: "OutboundClickToSD",
      tab,
      isExport
    }
  });
}
